import { Card, Col, Container, Row } from "react-bootstrap";
import { useLoaderData, useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { useState, useEffect } from "react";
import {
    ArrowRepeat,
    Calendar2Check,
    ChevronRight,
    PersonFill,
    QrCodeScan,
} from "react-bootstrap-icons";
import { authAtom, actingAsAtom } from "./Data/Atoms";
import { ServerRequestsGetParkerDashboardStatsResponse } from "./shared/api/axios-client";
import { OutstandingBalanceBanner } from "./Controls/OutstandingBalanceBanner";
import { UserProfileEditModal } from "./UserProfileEditModal";
import { showInstantLocal } from "./Data/Date";

interface StatCardProps {
    icon:        React.ReactNode;
    count:       number;
    title:       string;
    subtitle:    string;
    onClick:     () => void;
}

function StatCard( { icon, count, title, subtitle, onClick }: StatCardProps ) {
    const isEmpty = count === 0;
    const iconBg  = isEmpty ? "#e9ecef" : "var(--color-mid, #D61F84)";
    const iconColor = isEmpty ? "#adb5bd" : "#fff";
    const countColor = isEmpty ? "#adb5bd" : undefined;

    const iconEl = (
        <div
            className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 40, height: 40, backgroundColor: iconBg, color: iconColor }}
        >
            {icon}
        </div>
    );

    return (
        <Card
            className="rounded-3 shadow-subtle h-100"
            style={{ cursor: "pointer" }}
            onClick={onClick}
        >
            <Card.Body className="px-3 py-3">
                {/* Mobile: icon left, text center, chevron right */}
                <div className="d-flex d-md-none align-items-center gap-3">
                    {iconEl}
                    <div className="flex-grow-1 min-width-0">
                        <div className="fw-semibold" style={{ color: countColor }}>
                            <span className="fw-bold">{count}</span>{" "}{title}
                        </div>
                        <div className="text-muted small text-truncate">{subtitle}</div>
                    </div>
                    <ChevronRight className="text-muted flex-shrink-0" />
                </div>

                {/* Desktop: stacked layout */}
                <div className="d-none d-md-block">
                    <div className="d-flex align-items-start justify-content-between mb-2">
                        {iconEl}
                        <ChevronRight className="text-muted mt-1" />
                    </div>
                    <div className="fw-bold" style={{ fontSize: "1.75rem", lineHeight: 1.1, color: countColor }}>{count}</div>
                    <div className="fw-semibold mt-1">{title}</div>
                    <div className="text-muted small mt-1">{subtitle}</div>
                </div>
            </Card.Body>
        </Card>
    );
}

export function UserProfilePage() {
    const nav     = useNavigate();
    const auth    = useAtomValue( authAtom );
    const actingAs = useAtomValue( actingAsAtom );
    const stats   = useLoaderData() as ServerRequestsGetParkerDashboardStatsResponse | null;

    const [showEdit, setShowEdit] = useState( false );

    useEffect( () => {
        if ( !auth.account || !auth.isLoggedIn ) { nav( "/" ); return; }
        if ( actingAs ) { nav( "/user/plan" ); }
    }, [auth, actingAs, nav] );

    if ( !auth.account || !auth.isLoggedIn || actingAs ) return null;

    const firstName = auth.account.name.split( " " )[0];

    const activeSubscriptionCount          = stats?.activeSubscriptionCount          ?? 0;
    const activeOrUpcomingReservationCount = stats?.activeOrUpcomingReservationCount ?? 0;
    const activeAccessPassCount            = stats?.activeAccessPassCount            ?? 0;

    return (
        <>
            <UserProfileEditModal show={showEdit} onClose={() => setShowEdit( false )} />

            <Container fluid className="px-0">
                <div className="mb-3">
                    <div className="fw-bold" style={{ fontSize: "1.4rem" }}>Welcome back, {firstName}</div>
                    <div className="text-muted small">Here's what's happening with your account</div>
                </div>

                <OutstandingBalanceBanner className="mb-3" />


                <div className="text-muted small text-uppercase fw-semibold mb-2 d-md-none" style={{ letterSpacing: "0.04em" }}>
                    Your account
                </div>

                <Row className="align-items-stretch gy-3 mb-3">
                    <Col md={4}>
                        <StatCard
                            icon={<ArrowRepeat />}
                            count={activeSubscriptionCount}
                            title="Active plans"
                            subtitle="Manage subscriptions and billing"
                            onClick={() => nav( "/user/plan" )}
                        />
                    </Col>
                    <Col md={4}>
                        <StatCard
                            icon={<Calendar2Check />}
                            count={activeOrUpcomingReservationCount}
                            title="Reservations"
                            subtitle={activeOrUpcomingReservationCount === 0 ? "No upcoming reservations" : "View and manage your bookings"}
                            onClick={() => nav( "/user/reservation/upcoming" )}
                        />
                    </Col>
                    <Col md={4}>
                        <StatCard
                            icon={<QrCodeScan />}
                            count={activeAccessPassCount}
                            title="Access passes"
                            subtitle={activeAccessPassCount === 0 ? "No active passes yet" : "See active gate and entry passes"}
                            onClick={() => nav( "/user/access-codes" )}
                        />
                    </Col>
                </Row>

                <Card className="rounded-3 shadow-subtle mt-4">
                    <Card.Body className="d-flex align-items-center gap-3 px-3 py-2">
                        <div
                            className="rounded d-flex align-items-center justify-content-center text-muted flex-shrink-0 border"
                            style={{ width: 32, height: 32, backgroundColor: "#f8f9fa" }}
                        >
                            <PersonFill size={16} />
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                            <div className="fw-semibold small text-truncate">{auth.account.name}</div>
                            <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                                {auth.account.emailAddress}
                               
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-link text-decoration-none p-0 flex-shrink-0 d-inline-flex align-items-center gap-1"
                            style={{ color: "var(--color-mid, #D61F84)", fontSize: "0.78rem" }}
                            onClick={() => setShowEdit( true )}
                        >
                            Edit profile <ChevronRight size={11} />
                        </button>
                    </Card.Body>
                </Card>
            </Container>
        </>
    );
}
